import { compileFilterExpression } from "@tsmono/filter-expression";
import { inputString, totalModelFallbacks } from "@tsmono/inspect-common/utils";
import { arrayToString, isRecord } from "@tsmono/util";

import { EvalSampleScore } from "../../../@types/extraInspect";
import { FilterError, ScoreLabel } from "../../../app/types";
import { SampleSummary } from "../../../client/api/types";
import { totalSampleTokens } from "../../../client/utils/derive";
import { kScoreTypeBoolean } from "../../../constants";
import { SamplesDescriptor } from "../descriptor/samplesDescriptor";
import { EvalDescriptor, ScoreDescriptor } from "../descriptor/types";

import { kSampleMetadataPrefix } from "./sample-filter/language";

export interface SampleFilterItem {
  shortName?: string;
  qualifiedName?: string;
  canonicalName: string;
  tooltip?: string;
  categories: string[];
  // undefined when no samples carry the score (descriptor lookup misses)
  scoreType: string | undefined;
}

/**
 * Coerces a value to the type expected by the score.
 */
const coerceValue = (
  value: unknown,
  descriptor: ScoreDescriptor | undefined
): unknown => {
  if (descriptor && descriptor.scoreType === kScoreTypeBoolean) {
    return Boolean(value);
  } else {
    return value;
  }
};

// Whether a particular value is filter-able
const isFilteringSupportedForValue = (value: unknown): boolean =>
  ["string", "number", "boolean"].includes(typeof value) || value === null;

/**
 * Names the per-sample namespace always defines (see `sampleVariables`).
 * A score sharing one of these short names must be addressed by its
 * qualified `scorer.name` form: binding the bare name would shadow the
 * built-in — both for hand-typed expressions and for the column funnels
 * that compile through the same namespace (the epoch column's funnel
 * would silently filter on the score). Kept in sync with
 * `sampleVariables` by test.
 */
export const builtinFilterVariables: ReadonlySet<string> = new Set([
  "epoch",
  "has_error",
  "has_limit",
  "has_retries",
  "has_fallbacks",
  "completed",
  "id",
  "uuid",
  "input",
  "target",
  "answer",
  "error",
  "limit",
  "retries",
  "fallbacks",
  "tokens",
  "duration",
  "metadata",
]);

/**
 * Returns the names of scores that are not allowed to be used as short names
 * in filter expressions because they are not unique, or because they collide
 * with a built-in sample variable. This should be applied only to the nested
 * scores, not to the top-level scorer names.
 */
export const bannedShortScoreNames = (scores: ScoreLabel[]): Set<string> => {
  const used: Set<string> = new Set();
  const banned: Set<string> = new Set(builtinFilterVariables);
  for (const { scorer, name } of scores) {
    banned.add(scorer);
    if (used.has(name)) {
      banned.add(name);
    } else {
      used.add(name);
    }
  }
  return banned;
};

// Pseudo-variables added to all filter expressions. These are not needed in most cases.
// Normally one could check a boolean value `foo` by simply typing `foo` or `not foo`.
// However, some evals use tristate values that can be true, false or null. This is where
// these constants come in handy.
const filterExpressionConstants: Record<string, unknown> = {
  True: true,
  False: false,
  None: null,
};

/**
 * Generates a dictionary of variables that can be used in the filter expression.
 * High-level scorer metrics can be accessed by name directly.
 * Child metrics are accessed using dot notation (e.g. `scorer_name.score_name`) or
 * directly by name when it is unique.
 */
const scoreVariables = (
  evalDescriptor: EvalDescriptor,
  sampleScores: EvalSampleScore | null | undefined
): Record<string, unknown> => {
  const bannedShortNames = bannedShortScoreNames(evalDescriptor.scores);
  const variables: Record<string, unknown> = {};

  const addScore = (
    variableName: string,
    scoreLabel: ScoreLabel,
    value: unknown
  ): void => {
    const coercedValue = coerceValue(
      value,
      evalDescriptor.scoreDescriptor(scoreLabel)
    );
    if (isFilteringSupportedForValue(coercedValue)) {
      variables[variableName] = coercedValue;
    }
  };

  for (const [scorer, score] of Object.entries(sampleScores || {})) {
    // A scorer named after a built-in has no qualified fallback — skip the
    // binding rather than shadow the sample variable.
    if (!builtinFilterVariables.has(scorer)) {
      addScore(scorer, { scorer, name: scorer }, score.value);
    }
    if (typeof score.value === "object") {
      for (const [name, value] of Object.entries(score.value)) {
        addScore(`${scorer}.${name}`, { scorer, name }, value);
        if (!bannedShortNames.has(name)) {
          addScore(name, { scorer, name }, value);
        }
      }
    }
  }
  return variables;
};

const getNestedPropertyValue = (obj: unknown, path: string): unknown => {
  const keys = path.split(".");
  let current: unknown = obj;
  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      // Arrays step too (`list.length`, `list.0`), so plain record access
      // is not enough; Reflect.get reads any object member by string key.
      const member: unknown = Reflect.get(current, key);
      current = member;
    } else {
      return undefined;
    }
  }
  return current;
};

export const sampleVariables = (
  sample: SampleSummary,
  samplesDescriptor: SamplesDescriptor | undefined
): Record<string, unknown> => {
  return {
    epoch: sample.epoch,
    has_error: !!sample.error,
    has_limit: !!sample.limit,
    has_retries: (sample.retries ?? 0) > 0,
    has_fallbacks: totalModelFallbacks(sample.model_fallbacks) > 0,
    completed: sample.completed,
    id: sample.id,
    uuid: sample.uuid ?? null,
    input: inputString(sample.input).join(" "),
    target: arrayToString(sample.target),
    answer:
      samplesDescriptor?.selectedScorerDescriptor(sample)?.answer() ?? null,
    error: sample.error ?? null,
    limit: sample.limit ?? null,
    retries: sample.retries ?? 0,
    fallbacks: totalModelFallbacks(sample.model_fallbacks),
    tokens: totalSampleTokens(sample.model_usage) ?? null,
    duration: sample.total_time ?? null,
    metadata: sample.metadata,
  };
};

/**
 * Generates a dictionary of variables that can be used in the filter expression.
 * High-level scorer metrics can be accessed by name directly.
 * Child metrics are accessed using dot notation (e.g. `scorer_name.score_name`) or
 * directly by name when it is unique.
 */
export const sampleFilterItems = (
  evalDescriptor: EvalDescriptor
): SampleFilterItem[] => {
  const items: SampleFilterItem[] = [];
  const bannedShortNames = bannedShortScoreNames(evalDescriptor.scores);
  const valueToString = (value: unknown) =>
    typeof value === "string" ? `"${value}"` : String(value);

  const addScore = (
    scoreLabel: ScoreLabel,
    shortName?: string,
    qualifiedName?: string
  ) => {
    const canonicalName = shortName || qualifiedName;
    if (!canonicalName) {
      throw new Error("Unable to create a canonical name for a score");
    }
    const descriptor = evalDescriptor.scoreDescriptor(scoreLabel);

    if (!descriptor) {
      items.push({
        shortName,
        qualifiedName,
        canonicalName,
        tooltip: undefined,
        categories: [],
        scoreType: undefined,
      });
      return;
    }

    // This is not a filterable score
    if (descriptor.filterable === false) {
      return;
    }

    const scoreType = descriptor.scoreType;
    let tooltip = `${canonicalName}: ${descriptor.scoreType}`;
    let categories: string[] = [];
    if (descriptor.min !== undefined || descriptor.max !== undefined) {
      const rounded = (num: number) => {
        // Additional round-trip to remove trailing zeros.
        return parseFloat(num.toPrecision(3)).toString();
      };
      tooltip += `\nrange: ${rounded(descriptor.min || 0)} to ${rounded(descriptor.max || 0)}`;
    }
    if (descriptor.categories) {
      categories = descriptor.categories.map((cat) => {
        const val = isRecord(cat) ? cat["val"] : cat;
        return valueToString(val);
      });
      tooltip += `\ncategories: ${categories.join(" ")}`;
    }
    items.push({
      shortName,
      qualifiedName,
      canonicalName,
      tooltip,
      categories,
      scoreType,
    });
  };

  for (const { name, scorer } of evalDescriptor.scores) {
    // A top-level scorer named after a built-in has no addressable form at
    // all (no qualified fallback) — nothing to suggest.
    if (name === scorer && builtinFilterVariables.has(name)) {
      continue;
    }
    const hasShortName = name === scorer || !bannedShortNames.has(name);
    const hasQualifiedName = name !== scorer;
    const shortName = hasShortName ? name : undefined;
    const qualifiedName = hasQualifiedName ? `${scorer}.${name}` : undefined;
    addScore({ name, scorer }, shortName, qualifiedName);
  }
  return items;
};

// TODO: Add case-insensitive string comparison.
export const filterExpression = (
  samplesDescriptor: SamplesDescriptor,
  sample: SampleSummary,
  filterValue: string
) => {
  try {
    const evalDescriptor = samplesDescriptor.evalDescriptor;
    const inputContains = (regex: string): boolean => {
      return inputString(sample.input).some((msg) =>
        msg.match(new RegExp(regex, "i"))
      );
    };
    const targetContains = (regex: string): boolean => {
      const targets = Array.isArray(sample.target)
        ? sample.target
        : [sample.target];
      return targets.some((target) => target.match(new RegExp(regex, "i")));
    };
    const errorContains = (regex: string): boolean => {
      return !!sample.error?.match(new RegExp(regex, "i"));
    };
    const answerContains = (regex: string): boolean => {
      const answer = samplesDescriptor
        .selectedScorerDescriptor(sample)
        ?.answer();
      return !!answer?.match(new RegExp(regex, "i"));
    };

    const isNan = (value: unknown): boolean =>
      typeof value === "number" && Number.isNaN(value);

    const extraFunctions = {
      input_contains: inputContains,
      target_contains: targetContains,
      error_contains: errorContains,
      answer_contains: answerContains,
      is_nan: isNan,
    };
    const mySampleVariables = sampleVariables(sample, samplesDescriptor);
    const vars = {
      ...mySampleVariables,
      ...scoreVariables(evalDescriptor, sample.scores),
    };
    const resolveVariable = (name: string, get: (name: string) => unknown) => {
      // Sample variables (like has_error) always exist.
      if (name in mySampleVariables) {
        const value = get(name);
        return value;
      }
      // Handle metadata property access
      if (name.startsWith(kSampleMetadataPrefix)) {
        const propertyPath = name.substring(kSampleMetadataPrefix.length);
        return getNestedPropertyValue(sample.metadata, propertyPath);
      }
      // Score variables exist only if the sample completed successfully.
      return sample.error ? undefined : get(name);
    };
    const expression = compileFilterExpression(filterValue, {
      extraFunctions,
      constants: filterExpressionConstants,
      customProp: resolveVariable,
    });
    const result: unknown = expression(vars);
    if (typeof result === "boolean") {
      return { matches: result, error: undefined };
    } else if (result instanceof Error) {
      throw result;
    } else {
      throw new TypeError(
        `Filter expression returned a non-boolean value: ${String(result)}`
      );
    }
  } catch (error) {
    if (error instanceof ReferenceError) {
      // The expression evaluator attaches `propertyName` to the errors it
      // raises; a ReferenceError from anywhere else simply won't have one.
      const raised: unknown = error;
      const propertyName = isRecord(raised)
        ? ((): string => {
            const name = raised["propertyName"];
            return typeof name === "string" ? name : "";
          })()
        : "";
      if (propertyName) {
        // Don't show errors for metadata properties - they might not exist in all samples
        if (propertyName.startsWith(kSampleMetadataPrefix)) {
          return { matches: false, error: undefined };
        }
        const escaped = propertyName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`\\b${escaped}\\b`);
        const match = regex.exec(filterValue);
        if (match) {
          return {
            matches: false,
            error: {
              from: match.index,
              to: match.index + propertyName.length,
              message: error.message,
              severity: "warning",
            },
          };
        }
      }
    }

    const message = error instanceof Error ? error.message : "";
    if (
      message.startsWith("Parse error") ||
      message.startsWith("Lexical error")
    ) {
      // Syntax errors keep filtrex's formatting:
      //   foo and
      //   ----^
      const from = message.match(/^(-*)\^$/m)?.[1]?.length;
      return {
        matches: false,
        error: {
          from: from,
          message: "Syntax error",
          severity: "error",
        },
      };
    }

    return {
      matches: false,
      error: {
        message: message,
        severity: "error",
      },
    };
  }
};

export const filterSamples = (
  samplesDescriptor: SamplesDescriptor,
  samples: SampleSummary[],
  filterValue: string
): {
  result: SampleSummary[];
  error: FilterError | undefined;
  allErrors: boolean;
} => {
  let error = undefined;
  let errorCount = 0;
  const result = samples.filter((sample) => {
    if (filterValue) {
      const { matches, error: sampleError } = filterExpression(
        samplesDescriptor,
        sample,
        filterValue
      );
      error ||= sampleError;
      if (sampleError) {
        errorCount++;
      }
      return matches;
    } else {
      return true;
    }
  });
  return { result, error, allErrors: errorCount === samples.length };
};
